
import React, { Fragment, useState, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button }from '@cmsgov/design-system';


// Components

import ModalHeader from '../headers/ModalHeader';
import RemoveAndHandleView from './upload-modal-views/RemoveAndHandleView';
import UploadModalErrorMessage from './upload-modal-views/UploadModalErrorMessage';
import UploadView from './upload-modal-views/UploadView';

// Utils

import { convertSizeValueToString } from '../../../utils/miscellaneous/convertSizeValueToString';

// Constants

import { UPLOAD_MODAL_VIEWS } from '../../../constants/common/miscellaneous';


const UploadModal = props => {
    const {
        actions: {
            hideModal
        },
        modalHeaderProps,
        modalHeaderProps: {
            primaryTopAnchor
        },
        modal:{
            modalData: {
                config: {
                    acceptedFileTypes,
                    excludeFileExtensionList,
                    acceptedFileTypesValidation,
                    acceptedFileTypesVisualization,
                    modalHeader,
                    uploadedFileName: uploadedFileNameFromProps,
                    uploadedFileSize: uploadedFileSizeFromProps,
                    mainLabel,
                    maxFileSize,
                    maxNameLength,
                    fileNameValidator,
                    modalInfoItems,
                    upload: {
                        action: uploadAction,
                        payloadComponents: uploadPayloadComponents
                    },
                    removal: {
                        action: removalAction,
                        payloadComponents: removalPayloadComponents
                    },
                    view: viewFromProps,
                    rejectedDocuments
                }
            }
        },
        bottomAnchors: [
            anchor_1,
        ],
        boundAnchor
    } = props;

    const enhancedModalHeaderProps = {
        ...modalHeaderProps,
        modalHeaderTextData: modalHeader
            ? modalHeader : modalHeaderProps.modalHeaderTextData
    };

    const {
        HANDLE_DOCUMENT,
        REMOVE_DOCUMENT,
        UPLOAD_DOCUMENT
    } = UPLOAD_MODAL_VIEWS;

    const [ backEndError, toggleBackEndErrorVisibility ] = useState(false);
    const [ fileName, setFileName ] = useState(uploadedFileNameFromProps);
    const [ fileSize, setFileSize ] = useState(convertSizeValueToString(uploadedFileSizeFromProps));
    const [ sameUploadError, toggleSameUploadVisibility ] = useState(false);
    const [ sizeExceedingError, toggleSizeExceedingVisibility ] = useState(false);
    const [ view, toggleView ] = useState(viewFromProps);

    const isAnyError = useMemo(
        () => sizeExceedingError || !!backEndError || !!sameUploadError,
        [
            sizeExceedingError,
            backEndError,
            sameUploadError
        ]
    );

    const sizeLimit = useMemo(
        () => (maxFileSize / Math.pow(2, 20)).toFixed(2),
        [ maxFileSize ]
    );

    const handleResponse = (res, successCallback, failureCallback) => {
        const {
            success,
            message
        } = res;

        success ? successCallback(): failureCallback(message);
    };

    const someParentActions = {
        handleResponse,
        setFileName,
        setFileSize,
        toggleBackEndErrorVisibility,
        toggleView
    };

    const getView = () => {
        switch(view){
            case UPLOAD_DOCUMENT:
                return (
                    <UploadView
                        { ...{
                            excludeFileExtensionList,
                            acceptedFileTypes,
                            acceptedFileTypesValidation,
                            acceptedFileTypesVisualization,
                            isAnyError,
                            maxFileSize,
                            maxNameLength,
                            fileNameValidator,
                            parentActions: {
                                ...someParentActions,
                                toggleSameUploadVisibility,
                                toggleSizeExceedingVisibility,
                                uploadAction
                            },
                            primaryTopAnchor,
                            rejectedDocuments,
                            sameFileUploadCheckEnabled: false,
                            uploadPayloadComponents,
                            sizeLimit
                        } }
                    />
                );
            case REMOVE_DOCUMENT:
            case HANDLE_DOCUMENT:
                return(
                    <RemoveAndHandleView
                        { ...{
                            backEndError,
                            fileName,
                            fileSize,
                            parentActions: {
                                boundAnchor,
                                ...someParentActions,
                                removalAction
                            },
                            primaryTopAnchor,
                            removalPayloadComponents,
                            view
                        } }
                    />
                );
        }
    };

    const getInfoItems = () => {
        return modalInfoItems.map(({ label, value }, idx) => {
            return (
                <Fragment key = { `${label}_${idx}` }>
                    <div className='label-value-pair'>
                        <p className = 'label-value-pair__label'>{ label }</p>
                        <p className = 'label-value-pair__value'>{ value }</p>
                    </div>
                </Fragment>
            );
        });
    };

    return (
        <Fragment>
            <div className = 'gi-modal__header__head'>
                <ModalHeader { ...{ ...enhancedModalHeaderProps }} />
            </div>
            <div className = 'gi-modal__body'>
                {
                    getInfoItems()
                }
                <div className='label--standalone pull-left'>
                    <FormattedMessage id = { mainLabel } />
                </div>

                {
                    getView()
                }

                <div className = 'file-upload-module__subdescription'>
                    <span className='pull-left'>
                        <FormattedMessage
                            id = 'modals.accepted_files_types'
                            values = {{ acceptedFileTypesVisualization }}
                        />
                    </span>
                    <span className='pull-right'>
                        <FormattedMessage
                            id = 'modals.max_file_size'
                            values = {{ sizeLimit }}
                        />
                    </span>
                </div>
                <UploadModalErrorMessage
                    { ...{
                        backEndError,
                        isAnyError,
                        parentActions: {
                            toggleBackEndErrorVisibility,
                            toggleSameUploadVisibility,
                            toggleSizeExceedingVisibility,
                        },
                        primaryTopAnchor,
                        sameUploadError,
                        sizeExceedingError,
                        sizeLimit
                    } }
                />
            </div>
            <div className = 'gi-modal__controls'>
                <Button
                    className = 'usa-button gi-button gi-button__primary'
                    onClick = { hideModal }
                    inputRef = { boundAnchor(anchor_1) }
                >
                    <FormattedMessage id = 'button.close'/>
                </Button>
            </div>
        </Fragment>
    );
};

export default UploadModal;